import React, { useState, useEffect } from 'react';
import { ProductsService, type Product, type Presentacion } from '../../services/products/products.service';
import { SuppliersService, type Proveedor } from '../../services/suppliers/suppliers.service';
import { usePermissions } from '../../hooks/usePermissions';
import { NotificationToast } from '../ui/NotificationToast';
import { Package, Plus, X, Loader2, Calendar, Hash, Boxes, Store, FileText, ToggleLeft, ToggleRight, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLoteDefaults } from '../../hooks/useLoteDefaults';

interface AddLoteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  product?: Product | null;
}

export const AddLoteDrawer: React.FC<AddLoteDrawerProps> = ({
  isOpen, onClose, onSuccess, product
}) => {
  const { hasPermission } = usePermissions();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{ title: string; message: string; type: 'success' | 'error' | 'warning' | 'info'; } | null>(null);

  const { defaults, setDefaults, toggleActivo } = useLoteDefaults();
  const [presentacionId, setPresentacionId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [numeroLote, setNumeroLote] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [numeroFactura, setNumeroFactura] = useState('');
  const [costoUnitario, setCostoUnitario] = useState<number | ''>('');
  const [suppliers, setSuppliers] = useState<Proveedor[]>([]);

  const selectedPresentacion = product?.presentaciones?.find(p => p.id === presentacionId);
  const unidadesBase = selectedPresentacion ? cantidad * selectedPresentacion.factor : 0;

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setPresentacionId('');
        setCantidad(1);
        setNumeroLote('');
        setFechaVencimiento('');
        setProveedorId('');
        setNumeroFactura('');
        setCostoUnitario('');
        setSuppliers([]);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (product?.presentaciones?.length) {
      const principal = product.presentaciones.find(p => p.esPrincipal) || product.presentaciones[0];
      setPresentacionId(principal.id);
    }
  }, [product]);

  // Cargar proveedores activos al abrir
  useEffect(() => {
    if (isOpen) {
      SuppliersService.getAllForSelect().then(res => {
        if (res.success) setSuppliers(res.data);
      });
    }
  }, [isOpen]);

  // Precargar proveedor/factura desde defaults globales
  useEffect(() => {
    if (isOpen && defaults.activo) {
      setProveedorId(defaults.proveedor);
      setNumeroFactura(defaults.numeroFactura);
    }
  }, [isOpen, defaults.activo, defaults.proveedor, defaults.numeroFactura]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !presentacionId || !numeroLote.trim() || cantidad <= 0) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setIsLoading(true);
    try {
      const proveedorNombre = proveedorId
        ? suppliers.find(s => s.id === proveedorId)?.nombre
        : undefined;
      const response = await ProductsService.addLote(product.id, {
        presentacionId,
        numeroLote: numeroLote.trim(),
        cantidad,
        costoUnitario: costoUnitario !== '' ? Number(costoUnitario) : undefined,
        fechaVencimiento: fechaVencimiento || undefined,
        proveedor: proveedorNombre,
        proveedorId: proveedorId || undefined,
        numeroFactura: numeroFactura.trim() || undefined,
      });

      // Si el modo global está activo, actualizar los defaults
      if (defaults.activo) {
        setDefaults({ proveedor: proveedorId, numeroFactura: numeroFactura.trim() });
      }

      if (response.success) {
        setNotificationConfig({
          title: 'Ingreso registrado',
          message: `Se agregaron ${unidadesBase} unidades base de ${product.nombre}`,
          type: 'success',
        });
        setShowNotification(true);
        onSuccess?.();
        setTimeout(() => onClose(), 500);
      } else {
        toast.error(response.message || 'Error al registrar ingreso');
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al registrar ingreso');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end">
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
        />
        <div
          className={`relative w-full max-w-md bg-white h-full shadow-2xl transform transition-transform duration-300 flex flex-col ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-gray-900">Nuevo ingreso</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {!hasPermission('Productos', 'crear stock') && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <Package className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Acceso restringido</p>
                  <p className="text-xs text-amber-700 mt-0.5">No tienes permiso para registrar ingresos de stock.</p>
                </div>
              </div>
            )}

            {product && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm font-medium text-emerald-800">{product.nombre}</p>
                <p className="text-xs text-emerald-600">
                  Stock actual: {product.stockBase || 0} unidades base
                </p>
              </div>
            )}

            <form id="lote-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Presentación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Presentación comprada *
                </label>
                <select
                  value={presentacionId}
                  onChange={(e) => setPresentacionId(e.target.value)}
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {product?.presentaciones?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} (factor {p.factor})
                    </option>
                  ))}
                </select>
              </div>

              {/* Cantidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad comprada *
                </label>
                <input
                  type="number"
                  min={1}
                  value={cantidad}
                  onChange={(e) => setCantidad(Number(e.target.value))}
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  required
                />
              </div>

              {/* Preview unidades base */}
              {selectedPresentacion && cantidad > 0 && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Boxes className="w-4 h-4 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      +{unidadesBase} unidades base
                    </p>
                    <p className="text-xs text-blue-600">
                      {cantidad} × factor {selectedPresentacion.factor} = {unidadesBase}
                    </p>
                  </div>
                </div>
              )}

              {/* Costo unitario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5" />
                    Costo unitario
                  </span>
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={costoUnitario}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCostoUnitario(val === '' ? '' : Number(val));
                  }}
                  placeholder="Ej: 8500"
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Costo de compra por unidad de esta presentación
                </p>
              </div>

              {/* Número de lote */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" />
                    Número de lote *
                  </span>
                </label>
                <input
                  type="text"
                  value={numeroLote}
                  onChange={(e) => setNumeroLote(e.target.value)}
                  placeholder="Ej: LOTE-2025-001"
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  required
                />
              </div>

              {/* Fecha de vencimiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Fecha de vencimiento
                  </span>
                </label>
                <input
                  type="date"
                  value={fechaVencimiento}
                  onChange={(e) => setFechaVencimiento(e.target.value)}
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>

              {/* Proveedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <Store className="w-3.5 h-3.5" />
                    Proveedor
                  </span>
                </label>
                <select
                  value={proveedorId}
                  onChange={(e) => setProveedorId(e.target.value)}
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option value="">Seleccionar proveedor...</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Número de factura */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" />
                    Número de factura
                  </span>
                </label>
                <input
                  type="text"
                  value={numeroFactura}
                  onChange={(e) => setNumeroFactura(e.target.value)}
                  placeholder="Ej: FAC-2025-001234"
                  disabled={!hasPermission('Productos', 'crear stock')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>

              {/* Toggle modo global */}
              <div
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  defaults.activo
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
                onClick={toggleActivo}
              >
                <div className="flex items-center gap-2">
                  {defaults.activo ? (
                    <ToggleRight className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <ToggleLeft className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${defaults.activo ? 'text-emerald-800' : 'text-gray-700'}`}>
                      Usar proveedor/factura por defecto
                    </p>
                    <p className="text-xs text-gray-500">
                      {defaults.activo
                        ? 'Se recordarán para los próximos ingresos'
                        : 'Cada ingreso usa valores independientes'}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${defaults.activo ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-200 text-gray-600'}`}>
                  {defaults.activo ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Info de defaults activos */}
              {defaults.activo && (defaults.proveedor || defaults.numeroFactura) && (
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <span className="font-medium">Valores guardados:</span>{' '}
                    {defaults.proveedor && (
                      <span>Proveedor: {suppliers.find(s => s.id === defaults.proveedor)?.nombre || defaults.proveedor}</span>
                    )}
                    {defaults.proveedor && defaults.numeroFactura && ' • '}
                    {defaults.numeroFactura && <span>Factura: {defaults.numeroFactura}</span>}
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="lote-form"
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                ) : (
                  <><Plus className="w-4 h-4" /> Registrar ingreso</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showNotification && notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};
